"use client"
import Image from "next/image";
import { ReactNode } from "react";

import Badge from "~/componentsDS/Badge";
import { QuizCTA } from "~/components/features/quiz";
import Carousel from "~/componentsDS/Carousel";
import ElementorContainer from "~/componentsDS/ElementorLoyout/ElementorContainer";
import PulseInfoButton from "~/componentsDS/PulseInfoButton";
import Reveal from "~/componentsDS/Reveal";
import Heading from "~/componentsDS/Typography/Heading";
import { CarouselContentVisible, CarouselItem } from "~/components/ui/carousel";
import { GTM_EVENT_NAMES } from "~/constants/gtmEvents";
import { QUIZ_ROUTES } from "~/constants/quizRoutes";
import { cn } from "~/lib/utils";

import HormoneCard from "~/componentsDS/HormoneCard";
import { scienceHormones } from "~/componentsDS/LPComponents/data";
import TextWithRegisteredMark from "~/componentsDS/TextWithRegisteredMark";
import useLpCtaText from "~/hooks/useLpCtaText";
import { useTrackEvent } from "~/hooks/useTrackEvent";
import { getTextFromNode } from "~/utils/getTextFromNode";

type Props = {
    buttonClassName?: string;
    sectionClassName?: string;
    headerElem?: ReactNode;
    ctaText?: string;
    diagramImageAlt?: string;
    cardImageAlts?: string[];
}

const defaultHeaderElem = <Heading as="h2" size="sm" sizes={{ md: "md" }} className="max-w-[550px]">How Menopause Oestra<sup>®</sup> supports your body</Heading>

const ScienceCardsSection = ({buttonClassName = "mt-8", sectionClassName, headerElem = defaultHeaderElem, ctaText = "Take the 2-min quiz", diagramImageAlt = "Oestra cream annotated with Progesterone, Estradiol, and Ellagē Base", cardImageAlts}: Props) => {
    const sendEvent = useTrackEvent();
    const ctaTitle = useLpCtaText(ctaText);

    const trackDotClick = (index: number) => {
        const hormone = scienceHormones[index];
        if (!hormone) return;

        sendEvent({
            event: "user_interaction",
            value: {
                event_name: GTM_EVENT_NAMES.LP_INTERACTION,
                widget: "our_science",
                meta_data: {
                    click_text: getTextFromNode(hormone.title),
                },
            },
        });
    };

    return (
        <section className={cn("pb-16 md:pb-[96px]", sectionClassName)}>
            <ElementorContainer innerClassName="flex flex-col">
                <Reveal className="text-primary500">
                    <Badge className="mb-4 font-medium">Our science</Badge>
                    {headerElem}
                </Reveal>

                <div className="relative flex justify-center items-center mb-4">
                    <div className="relative w-full max-w-[390px] md:max-w-[662px] aspect-[390/225]">
                        <Image
                            src="https://res.cloudinary.com/dirscuuhp/image/upload/v1774431749/new_design/Oestra_Magnific_Precision_Upscale_V2_2026-02-27_06-39-45_1_l634nb.webp"
                            alt={diagramImageAlt}
                            fill
                            sizes="1000px"
                            className="object-cover object-center"
                        />

                        <div className="absolute top-0 md:top-[50px] left-[50px]">
                            <PulseInfoButton
                                title="Progesterone"
                                analyticsTitle="Progesterone"
                                description="Protects the uterine lining, promotes restful sleep, balances estradiol, and supports a calm nervous system."
                                size="medium"
                            />
                        </div>
                        <div className="hidden md:block absolute top-[122px] right-[-70px]">
                            <PulseInfoButton
                                title="Estradiol"
                                analyticsTitle="Estradiol"
                                description="Engineered for vaginal absorption to enhance bioavailability, maintain natural pH, prevent leakage, and deliver stable systemic hormone levels."
                                direction="reverse"
                                size="medium"
                            />
                        </div>
                        <div className="md:hidden absolute top-[70px] left-[40px]">
                            <PulseInfoButton
                                title="Estradiol"
                                analyticsTitle="Estradiol"
                                description="Engineered for vaginal absorption to enhance bioavailability, maintain natural pH, prevent leakage, and deliver stable systemic hormone levels."
                                size="medium"
                            />
                        </div>
                        <div className="hidden md:block absolute top-[190px] left-[-40px]">
                            <PulseInfoButton
                                title={<>Ellagē<sup>®</sup> Base</>}
                                analyticsTitle="Ellagē® Base"
                                description="Supports brain, bones, metabolism, mood, vaginal tissue, and long-term vitality."
                                size="medium"
                            />
                        </div>
                        <div className="md:hidden absolute top-[155px] left-[120px]">
                            <PulseInfoButton
                                title={<>Ellagē<sup>®</sup> Base</>}
                                analyticsTitle="Ellagē® Base"
                                description="Supports brain, bones, metabolism, mood, vaginal tissue, and long-term vitality."
                                size="medium"
                                direction="reverse"
                            />
                        </div>
                    </div>
                </div>
                <div className="hidden lg:grid items-stretch grid-cols-3 gap-3">
                    {scienceHormones.map((hormone, index) => (
                        <div key={index} className={cn(index === 3 && "md:col-span-3")}>
                            <HormoneCard hormone={hormone} imageAlt={cardImageAlts?.[index] ?? hormone.imageAlt} variant={index === 3 ? "compact-wide" : "compact"} />
                        </div>
                    ))}
                </div>
            </ElementorContainer>

            <div className="lg:hidden box-content overflow-hidden pl-6 pr-5 mb-4">
                <Carousel
                    loop={false}
                    opts={{
                        align: "start",
                        skipSnaps: false,
                        dragFree: false
                    }}
                    variant="compact"
                    compactPaginationAlign="right"
                    compactPaginationBg="bg-pureWhite/10 backdrop-blur-[25px]"
                    compactPaginationDotColor="bg-gray100"
                    className="w-full"
                    onDotClick={trackDotClick}
                >
                    <CarouselContentVisible className="max-md:-ml-2">
                        {scienceHormones.map((hormone, index) => (
                            <CarouselItem key={index} className="max-md:pl-2 flex-shrink-0 basis-[min(100%,388px)] md:basis-[48%]">
                                <HormoneCard
                                    hormone={hormone}
                                    imageAlt={cardImageAlts?.[index] ?? hormone.imageAlt}
                                    variant="compact"
                                />
                            </CarouselItem>
                        ))}
                    </CarouselContentVisible>
                </Carousel>
            </div>

            <div id="science-cards-section" className={cn("flex justify-center px-6", buttonClassName)}>
                <QuizCTA
                    variant="primary"
                    eventData={{
                        event_name: GTM_EVENT_NAMES.LP_INTERACTION,
                        widget: "our_science",
                        meta_data: {
                            click_text: ctaText
                        },
                    }}
                    href={QUIZ_ROUTES.HORMONE_IMBALANCE}
                    size="md"
                    className="max-md:w-full"
                >
                    {<TextWithRegisteredMark text={ctaTitle} />}
                </QuizCTA>
            </div>
        </section>
    )
};

export default ScienceCardsSection;
